import React, { useState } from "react";
import { Wrench, Send, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { mantenimiento as api } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";
import CapturaFotos from "./CapturaFotos";

/**
 * SOLICITUDES DE MANTENIMIENTO.
 *
 * El residente reporta, la administración gestiona. Cada cambio de estado
 * queda con su autor y su fecha: eso es lo que evita el limbo de «ya lo
 * reporté hace un mes y nadie me dice nada», que es la queja real detrás de
 * este módulo.
 */

const FORM_VACIO = { type: "lampara", title: "", description: "", location: "", priority: "media" };
const COLOR = { recibido: "var(--gold)", pendiente: "var(--warning)", en_proceso: "var(--warning)", resuelto: "var(--success)", cerrado: "var(--text-tertiary)" };
const ESTADOS = ["recibido", "pendiente", "en_proceso", "resuelto", "cerrado"];

export default function Mantenimiento({ rol, catalogos, modulos }) {
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [creada, setCreada] = useState(null);

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const lista = datos?.requests || [];
  const esAdmin = rol === "admin";

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const guardar = async (e) => {
    e.preventDefault();
    setError(""); setGuardando(true);
    try {
      const r = await api.crear(form);
      setCreada(r.request);
      setForm(FORM_VACIO);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo enviar");
    } finally { setGuardando(false); }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Mantenimiento"
        subtitulo={esAdmin ? "Lo que la comunidad ha reportado." : "Reporte lo que hay que arreglar."}
        accion={esAdmin ? null : "Reportar"}
        alAccionar={() => { setCreada(null); setAbierta(true); }}
        icono={Wrench}
      />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : lista.length === 0 ? (
        <Vacio>No hay solicitudes.</Vacio>
      ) : (
        <div style={est.rejilla}>
          {lista.map((s) => (
            <div key={s._id} style={{ ...est.tarjeta, borderColor: COLOR[s.status] }}>
              <h3 style={est.tarjetaTitulo}>{s.title}</h3>
              <div style={{ ...est.tarjetaDato, color: COLOR[s.status], fontWeight: 600 }}>
                {s.status.replace("_", " ")} · prioridad {s.priority}
              </div>
              {s.description && <div style={{ ...est.tarjetaDato, display: "block" }}>{s.description}</div>}
              {s.location && <div style={est.tarjetaDato}>Lugar: {s.location}</div>}
              {esAdmin && s.reportedBy?.name && (
                <div style={est.tarjetaDato}>
                  {s.reportedBy.name} · Casa {s.polygon}-{s.houseNumber}
                </div>
              )}
              {s.updates?.length > 0 && (
                <div style={est.tarjetaDato}>Último: {s.updates[s.updates.length - 1].text}</div>
              )}

              {esAdmin && (
                <div style={est.tarjetaAcciones}>
                  <select
                    style={{ ...est.control, minHeight: "36px", padding: "var(--space-2)" }}
                    value={s.status}
                    onChange={(e) => api.actualizar(s._id, { status: e.target.value }).then(recargar)}
                  >
                    {ESTADOS.map((x) => (
                      <option key={x} value={x}>{x.replace("_", " ")}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo={creada ? "Solicitud enviada" : "Reportar mantenimiento"}
        abierta={abierta}
        alCerrar={() => { setAbierta(false); setCreada(null); }}
        pie={
          creada ? (
            <Button variant="primary" onClick={() => { setAbierta(false); setCreada(null); }}>Listo</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
              <Button variant="primary" onClick={guardar} loading={guardando} leftIcon={Send}>Enviar</Button>
            </>
          )
        }
      >
        {error && <Aviso>{error}</Aviso>}
        {creada ? (
          <>
            <p style={est.subtitulo}>
              La administración ya la tiene. Si puede, agregue una foto: «la lámpara de la esquina»
              genera una visita para averiguar cuál; una foto la resuelve.
            </p>
            {modulos?.fotos ? (
              <CapturaFotos ambito="mantenimiento" recursoId={creada._id}
                tipos={[{ valor: "otro", etiqueta: "Del problema" }]} />
            ) : (
              <Aviso>Su residencial tiene desactivadas las fotografías.</Aviso>
            )}
          </>
        ) : (
          <>
            <Seleccion etiqueta="Qué es" value={form.type} onChange={cambiar("type")}
              opciones={catalogos?.tiposMantenimiento || []} />
            <Entrada etiqueta="Título" value={form.title} onChange={cambiar("title")}
              placeholder="Lámpara quemada frente a la casa 12" required />
            <Area etiqueta="Detalle" value={form.description} onChange={cambiar("description")} />
            <div style={est.fila}>
              <Entrada etiqueta="Dónde" value={form.location} onChange={cambiar("location")} />
              <Seleccion etiqueta="Prioridad" value={form.priority} onChange={cambiar("priority")}
                opciones={[
                  { valor: "baja", etiqueta: "Baja" },
                  { valor: "media", etiqueta: "Media" },
                  { valor: "alta", etiqueta: "Alta" },
                ]} />
            </div>
          </>
        )}
      </Ventana>
    </div>
  );
}
